/**
 * AdminSupportSessions.tsx
 * ----------------------------------------------------------------------------
 * Página /admin/sessions — visão global (Admin Root) das sessões de suporte WL
 * executadas em qualquer tenant. Usa as RPCs admin_list_support_sessions e
 * admin_get_support_session_details.
 *
 * Layout idêntico a /wl/sessions (shell ListPageLayout), trocando apenas o
 * hook de dados e o scope do drawer para "admin".
 *
 * RELEASE: 2026-05-30 v1.1.0 - Refator visual ListPageLayout + filtros
 *                              Tenant/Operador + KPIs clicáveis.
 * RELEASE: 2026-05-30 v1.0.0 - Versão inicial.
 */
import { useMemo, useState } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  KeyRound,
  Search,
  Activity,
  CheckCircle2,
  AlertTriangle,
  RefreshCw,
} from "lucide-react";
import {
  ListPageLayout,
  ListPageHeader,
  ListPageKpis,
  KpiCard,
  ListPageFiltersBar,
  LIST_FILTERS_INPUT_CLASS,
  LIST_FILTERS_SELECT_TRIGGER_CLASS,
  ListPageContent,
  ListPageFooter,
} from "@/components/layout/list-page";
import { PaginationFooter } from "@/components/ui/pagination-footer";
import { CrmPeriodFilter } from "@/components/crm/filters/CrmPeriodFilter";
import {
  calculatePeriodDates,
  type PeriodFilter,
  type PeriodPreset,
} from "@/hooks/useCrmOverviewFilters";
import { cn } from "@/lib/utils";
import { SupportSessionsTable } from "@/components/wl/SupportSessionsTable";
import { SupportSessionDetailsDrawer } from "@/components/wl/SupportSessionDetailsDrawer";
import {
  useAdminSupportSessions,
  type SupportSessionRow,
  type SupportSessionStatus,
} from "@/hooks/wl/useSupportSessionsAudit";
import {
  useTenantsLookup,
  useOperatorsLookup,
} from "@/hooks/wl/useSupportSessionsLookups";

const PAGE_SIZE_OPTIONS = [50, 100, 200, 500];

export default function AdminSupportSessions() {
  // ---------- Estados de filtro ----------
  const [statusFilter, setStatusFilter] = useState<"__all" | SupportSessionStatus>("__all");
  const [tenantFilter, setTenantFilter] = useState<string>("__all");
  const [operatorFilter, setOperatorFilter] = useState<string>("__all");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const [kpiFilter, setKpiFilter] = useState<SupportSessionStatus | null>(null);

  const [period, setPeriod] = useState<PeriodFilter>(() => {
    const { startDate, endDate } = calculatePeriodDates("month");
    return { preset: "month", startDate, endDate };
  });
  const [periodActive, setPeriodActive] = useState(false);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const { data: tenants = [] } = useTenantsLookup();
  const { data: operators = [] } = useOperatorsLookup();

  const effectiveStatus = useMemo<SupportSessionStatus | null>(() => {
    if (kpiFilter) return kpiFilter;
    return statusFilter === "__all" ? null : statusFilter;
  }, [statusFilter, kpiFilter]);

  const periodIso = useMemo(
    () => ({
      from: periodActive ? period.startDate.toISOString() : null,
      to: periodActive ? period.endDate.toISOString() : null,
    }),
    [period, periodActive],
  );

  const filters = useMemo(
    () => ({
      status: effectiveStatus,
      tenantId: tenantFilter === "__all" ? null : tenantFilter,
      operatorUserId: operatorFilter === "__all" ? null : operatorFilter,
      search: search || null,
      startedFrom: periodIso.from,
      startedTo: periodIso.to,
      limit: pageSize,
      offset: (page - 1) * pageSize,
    }),
    [effectiveStatus, tenantFilter, operatorFilter, search, periodIso, pageSize, page],
  );

  const q = useAdminSupportSessions(filters);
  const rows = q.data ?? [];
  const total = Number(rows[0]?.total_count ?? 0);

  const kpis = useMemo(() => {
    const active = rows.filter((r) => r.status === "active").length;
    const ended = rows.filter((r) => r.status === "ended").length;
    const expired = rows.filter((r) => r.status === "expired").length;
    return { total, active, ended, expired };
  }, [rows, total]);

  const toggleKpi = (key: SupportSessionStatus | null) => {
    setKpiFilter((prev) => (prev === key ? null : key));
    setPage(1);
  };

  const handleRowClick = (row: SupportSessionRow) => {
    setSelectedId(row.session_id);
    setDrawerOpen(true);
  };

  const handlePresetChange = (preset: PeriodPreset) => {
    if (preset === "custom") return;
    const { startDate, endDate } = calculatePeriodDates(preset);
    setPeriod({ preset, startDate, endDate });
    setPeriodActive(true);
    setPage(1);
  };
  const handleCustomChange = (startDate: Date, endDate: Date) => {
    setPeriod({ preset: "custom", startDate, endDate });
    setPeriodActive(true);
    setPage(1);
  };

  return (
    <ListPageLayout>
      <ListPageHeader
        title="Sessões de Suporte"
        description="Visão global de acessos de suporte WL realizados em todos os tenants."
        actions={
          <Button
            variant="outline"
            size="sm"
            onClick={() => q.refetch()}
            disabled={q.isFetching}
          >
            <RefreshCw className={cn("h-4 w-4", q.isFetching && "animate-spin")} />
          </Button>
        }
      />

      <ListPageKpis columns={4}>
        <KpiCard
          color="purple"
          icon={KeyRound}
          label="Total"
          value={q.isLoading ? "…" : kpis.total}
          onClick={() => toggleKpi(null)}
          active={kpiFilter === null && statusFilter === "__all"}
        />
        <KpiCard
          color="green"
          icon={Activity}
          label="Ativas (página)"
          value={q.isLoading ? "…" : kpis.active}
          onClick={() => toggleKpi("active")}
          active={kpiFilter === "active"}
        />
        <KpiCard
          color="cyan"
          icon={CheckCircle2}
          label="Encerradas (página)"
          value={q.isLoading ? "…" : kpis.ended}
          onClick={() => toggleKpi("ended")}
          active={kpiFilter === "ended"}
        />
        <KpiCard
          color="orange"
          icon={AlertTriangle}
          label="Expiradas (página)"
          value={q.isLoading ? "…" : kpis.expired}
          onClick={() => toggleKpi("expired")}
          active={kpiFilter === "expired"}
        />
      </ListPageKpis>

      <ListPageFiltersBar isFetching={q.isFetching}>
        <div className="relative flex-1 min-w-[200px] sm:flex-none sm:w-[260px]">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 sm:h-4 sm:w-4 text-muted-foreground" />
          <Input
            className={cn(LIST_FILTERS_INPUT_CLASS, "pl-7 sm:pl-8")}
            placeholder="Buscar pelo motivo…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                setSearch(searchInput);
                setPage(1);
              }
            }}
            onBlur={() => {
              if (searchInput !== search) {
                setSearch(searchInput);
                setPage(1);
              }
            }}
          />
        </div>

        <Select
          value={statusFilter}
          onValueChange={(v) => {
            setStatusFilter(v as typeof statusFilter);
            setKpiFilter(null);
            setPage(1);
          }}
        >
          <SelectTrigger className={cn(LIST_FILTERS_SELECT_TRIGGER_CLASS, "sm:w-[130px]")}>
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all">Todos status</SelectItem>
            <SelectItem value="active">Ativa</SelectItem>
            <SelectItem value="ended">Encerrada</SelectItem>
            <SelectItem value="expired">Expirada</SelectItem>
          </SelectContent>
        </Select>

        <Select
          value={tenantFilter}
          onValueChange={(v) => {
            setTenantFilter(v);
            setPage(1);
          }}
        >
          <SelectTrigger className={cn(LIST_FILTERS_SELECT_TRIGGER_CLASS, "sm:w-[180px]")}>
            <SelectValue placeholder="Tenant" />
          </SelectTrigger>
          <SelectContent className="max-h-[300px]">
            <SelectItem value="__all">Todos tenants</SelectItem>
            {tenants.map((t) => (
              <SelectItem key={t.id} value={t.id}>
                {t.name}
                {t.slug ? ` (${t.slug})` : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={operatorFilter}
          onValueChange={(v) => {
            setOperatorFilter(v);
            setPage(1);
          }}
        >
          <SelectTrigger className={cn(LIST_FILTERS_SELECT_TRIGGER_CLASS, "sm:w-[180px]")}>
            <SelectValue placeholder="Operador" />
          </SelectTrigger>
          <SelectContent className="max-h-[300px]">
            <SelectItem value="__all">Todos operadores</SelectItem>
            {operators.map((op) => (
              <SelectItem key={op.user_id} value={op.user_id}>
                {op.display_name || op.email || op.user_id.slice(0, 8).toUpperCase()}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <CrmPeriodFilter
          value={period}
          onPresetChange={handlePresetChange}
          onCustomChange={handleCustomChange}
          className="h-6 text-[11px] sm:h-8 sm:text-sm"
        />
      </ListPageFiltersBar>

      <ListPageContent fullBleed>
        <SupportSessionsTable
          rows={rows}
          loading={q.isLoading}
          onRowClick={handleRowClick}
        />
      </ListPageContent>

      <ListPageFooter>
        <PaginationFooter
          totalItems={total}
          currentPage={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={(s) => {
            setPageSize(s);
            setPage(1);
          }}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
        />
      </ListPageFooter>

      <SupportSessionDetailsDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        sessionId={selectedId}
        scope="admin"
      />
    </ListPageLayout>
  );
}
